import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Mail, Lock, Eye, EyeOff, Loader2, Check, Circle, Heart, X } from "lucide-react";
import { AppStoreBadge } from "@/components/AppStoreBadge";
import { cn } from "@/lib/utils";
import { markGuestBrowse } from "@/lib/guestBrowsing";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { isNativeShell, getOAuthRedirectUri } from "@/lib/oauthRedirect";
import { isNativeApp } from "@/lib/platform";
import { tryPopupOAuth } from "@/lib/oauthPopup";
import { toast } from "sonner";
import { getAnyPushTarget } from "@/lib/pushNavigation";
import { isOnboardingCompleteOnDevice, markOnboardingCompleteOnDevice } from "@/lib/onboardingFlag";
import { getStoredReferralCode, clearStoredReferralCode } from "@/lib/referral";
import { useSwipeGesture } from "@/hooks/useSwipeGesture";
import { useKeyboardInset } from "@/hooks/useKeyboardInset";
import { prioritiesToImportance } from "@/lib/importanceScale";
import { filterToNullColumns, hasNoAnswers } from "@/lib/preferenceReplay";
import { PASSWORD_REQUIREMENTS } from "@/lib/passwordPolicy";
import { usePasswordLeakCheck, LEAK_CHECK_LABEL } from "@/lib/leakedPassword";
import { friendlyPasswordError } from "@/lib/authErrors";
import { suggestUsername, fallbackHandleFromId } from "@/lib/usernamePolicy";
import confetti from "canvas-confetti";
import posthog from "posthog-js";
import { setUserSegmentProperties } from "@/lib/analytics";
import { GradeStrip } from "@/components/browse/GradeStrip";
import { NotificationsScreen } from "@/components/onboarding/NotificationsScreen";
import { shouldOfferNotificationsScreen } from "@/lib/notificationsOnboarding";
import { GROUNDY } from "@/components/onboarding/groundyArt";
import { WelcomeGroundy } from "@/components/onboarding/WelcomeGroundy";
import { LocationStep } from "@/components/onboarding/LocationStep";
import { PicksStep } from "@/components/onboarding/PicksStep";
import { WaitlistStep } from "@/components/onboarding/WaitlistStep";
import { PickCard } from "@/components/onboarding/PickCard";
import mascotNormal from "@/assets/mascot-normal.png";
import {
  ForYouTag,
  MissingLine,
  OButton,
  OC,
  ScreenFrame,
  StepHeader,
  StepHeading,
  StepLine,
  StepTopBar,
  TermsLine,
  TextLink,
} from "@/components/onboarding/onboardingUi";
import { SCREEN, LAST_SCREEN, stepNameFor, progressFor, hasBackArrow } from "@/lib/onboardingSteps";
import {
  OCCUPATION_OPTIONS,
  AGE_RANGE_OPTIONS,
  FREQUENCY_OPTIONS,
  REFERRAL_OPTIONS,
  sanitizeAboutYou,
  aboutYouMissingLine,
} from "@/lib/onboardingAnswers";
import {
  getPendingLocation,
  getPendingPicks,
  setPendingLocation,
  clearPendingLocation,
  markLocationAnswered,
  setPendingTerms,
  clearPendingTerms,
} from "@/lib/onboardingPending";
import { useAreaCoverageProbe } from "@/hooks/useAreaCoverageProbe";
import type { HomeLocation } from "@/lib/homeLocation";

const PENDING_PREFERENCES_KEY = "grounded_pending_preferences";
// Garbage collection bound for the pending preferences cache, not a
// correctness gate. When the cache carries the confirming user's id we replay
// regardless of age.
const PENDING_PREFERENCES_TTL_MS = 30 * 24 * 60 * 60 * 1000;

// Example café on the Grounded Score screen: a real amenity combination that
// scores 87 (free WiFi 35, some outlets 20, large seating 20, free garage 12).
const SAMPLE_87_AMENITIES = {
  has_wifi: true,
  wifi_type: "free",
  outlet_availability: "some",
  seating_capacity: "large",
  parking_available: "free_garage",
};

/** Featured café on Welcome. Shown at once, then replaced by the live row. */
export const WELCOME_CAFE_SLUG = "espressoteric-coffee-co-los-angeles-ca-1766722259635";
const WELCOME_CAFE_FALLBACK = { name: "Espressoteric Coffee Co.", neighborhood: "Westwood", photo: null as string | null, score: null as number | null };

const SANS = "'DM Sans', sans-serif";

// While onboarding is open, html and body match the flow background so a
// scroll or overscroll never shows a different color. On close, the exact
// previous inline values (including none) are put back.
const ONBOARDING_PAGE_BG = OC.bg;
function useOnboardingPageBackground() {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.backgroundColor;
    const prevBody = body.style.backgroundColor;
    html.style.backgroundColor = ONBOARDING_PAGE_BG;
    body.style.backgroundColor = ONBOARDING_PAGE_BG;
    return () => {
      html.style.backgroundColor = prevHtml;
      body.style.backgroundColor = prevBody;
    };
  }, []);
}

function capture(event: string, props?: Record<string, unknown>) {
  try { posthog.capture(event, props); } catch { /* ignore */ }
}

// ─── Google / Apple logos ───
const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);
const AppleLogo = () => (
  <svg width="16" height="16" viewBox="0 0 814 1000" fill="white" aria-hidden="true">
    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57.8-155.5-127.4c-58.3-81.3-105.3-207-105.3-326.4C0 413 56.3 302.6 142.8 237.5c83.2-62.4 163.2-77.9 218.3-77.9 67.7 0 123.9 44 165.9 44 40.7 0 104.3-46.7 181.1-46.7 29.3 0 134.8 2.6 204 99.9zM554.1 159.4c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 103.3-30.4 135.5-71.3z" />
  </svg>
);

/** Name an OAuth provider handed over, if any. Never invents a last name. */
function nameFromMetadata(meta: Record<string, unknown> | undefined): { first: string; last: string } {
  if (!meta) return { first: "", last: "" };
  const first = typeof meta.first_name === "string" ? meta.first_name : "";
  const last = typeof meta.last_name === "string" ? meta.last_name : "";
  if (first) return { first, last };
  const full = [meta.full_name, meta.name].find((v) => typeof v === "string" && v.trim()) as string | undefined;
  if (!full) return { first: "", last: "" };
  const parts = full.trim().split(/\s+/);
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

// ─── Finish screen (own component so hooks are legal) ───
const FinishScreen = ({ onFinish, username }: { onFinish: () => void; username: string | null }) => {
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    const colors = ["#3b2314", "#7b4f2e", "#c4a882", "#f5ecd7"];
    try {
      confetti({ particleCount: 120, spread: 80, startVelocity: 45, origin: { x: 0.5, y: 0.1 }, colors, zIndex: 9999, scalar: 0.9 });
    } catch { /* ignore */ }
    const t = window.setTimeout(() => {
      try {
        confetti({ particleCount: 60, spread: 100, startVelocity: 35, origin: { x: 0.5, y: 0.15 }, colors, zIndex: 9999, scalar: 0.8 });
      } catch { /* ignore */ }
    }, 600);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <ScreenFrame
      testId="finish-step"
      dock={
        <>
          <OButton onClick={onFinish}>Take me there →</OButton>
          <p className="text-center mt-4" style={{ fontSize: 12, color: OC.textSec }}>
            Made with <span aria-label="love" style={{ color: "#D64545" }}>♥</span> by remote workers
          </p>
        </>
      }
    >
      <div className="flex flex-col items-center text-center pt-6">
        <img src={GROUNDY.finish} alt="" aria-hidden="true" className="h-[200px] w-auto" />
        <div className="mt-4"><StepHeading center>You're in.</StepHeading></div>
        <StepLine center className="max-w-[280px]">Your curated café list is ready. Let's find your spot.</StepLine>
        <p className="text-center max-w-[300px] mt-2 text-pretty" style={{ fontSize: 13, color: OC.muted }}>
          Hundreds of remote workers have already found their spot. Now it's your turn.
        </p>
        {username && (
          <div className="mt-5 w-full rounded-2xl px-4 py-3" style={{ backgroundColor: OC.tile, fontSize: 13, color: OC.textSec }} data-testid="finish-username">
            You're <span style={{ fontWeight: 700, color: OC.text }}>@{username}</span>. You can change it in your profile.
          </div>
        )}
      </div>
    </ScreenFrame>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

interface OnboardingFlowProps {
  /** Screen to open at; Index passes About you when an unfinished account resumes. */
  initialScreen?: number;
}

const OnboardingFlow = ({ initialScreen }: OnboardingFlowProps = {}) => {
  useKeyboardInset();
  useOnboardingPageBackground();
  const navigate = useNavigate();
  const { user } = useAuth();
  const wasInitiallyAuthenticated = useRef(!!user);
  // Force paths (?onboarding=1|true, ?resetOnboarding=1) are QA/debug entries.
  const isForceOnboarding = useRef(
    typeof window !== "undefined" && (() => {
      const p = new URLSearchParams(window.location.search);
      return p.get("onboarding") === "1" || p.get("onboarding") === "true" || p.get("resetOnboarding") === "1";
    })(),
  );
  const [currentScreen, setCurrentScreen] = useState(() => {
    // Preview shortcut: ?screen=N jumps directly to that screen.
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const n = Number(params.get("screen"));
      if (params.has("screen") && Number.isInteger(n) && n >= 0 && n <= LAST_SCREEN) return n;
    }
    return initialScreen ?? SCREEN.welcome;
  });
  const [slideDirection, setSlideDirection] = useState<"left" | "right">("left");

  // Score animation
  const [scoreAnimated, setScoreAnimated] = useState(false);
  const [displayScore, setDisplayScore] = useState(0);
  const [scoreKey, setScoreKey] = useState(0);

  // Answers
  const [selectedWorkPriorities, setSelectedWorkPriorities] = useState<string[]>([]);
  const [location, setLocation] = useState<HomeLocation | null>(() => getPendingLocation());
  const [selectedOccupation, setSelectedOccupation] = useState("");
  const [selectedAgeRange, setSelectedAgeRange] = useState("");
  const [selectedCafeFrequency, setSelectedCafeFrequency] = useState("");
  const [selectedReferralSource, setSelectedReferralSource] = useState("");
  const [aboutSaving, setAboutSaving] = useState(false);
  const [finishUsername, setFinishUsername] = useState<string | null>(null);

  // Account
  const [emailMode, setEmailMode] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [resending, setResending] = useState(false);

  const inOnboardingRef = useRef(true);
  const currentScreenRef = useRef(currentScreen);
  useEffect(() => { currentScreenRef.current = currentScreen; }, [currentScreen]);

  const goTo = useCallback((target: number) => {
    if (target < 0 || target > LAST_SCREEN) return;
    setSlideDirection(target >= currentScreenRef.current ? "left" : "right");
    setCurrentScreen(target);
  }, []);

  // ─── Welcome café: live row, never waited on ───
  const [welcomeCafe, setWelcomeCafe] = useState(WELCOME_CAFE_FALLBACK);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("cafes")
          .select("name, neighborhood, cover_image_url, work_readiness_score")
          .eq("slug", WELCOME_CAFE_SLUG)
          .maybeSingle();
        if (cancelled || error || !data) return;
        const row = data as { name?: string | null; neighborhood?: string | null; cover_image_url?: string | null; work_readiness_score?: number | null };
        setWelcomeCafe({
          name: row.name || WELCOME_CAFE_FALLBACK.name,
          neighborhood: row.neighborhood || WELCOME_CAFE_FALLBACK.neighborhood,
          photo: row.cover_image_url || null,
          score: typeof row.work_readiness_score === "number" ? row.work_readiness_score : null,
        });
      } catch { /* keep the fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // ─── Your picks or No cafés nearby ───
  const hasCoords = !!location && typeof location.lat === "number" && typeof location.lng === "number";
  const { hasCoverageNearby, isChecking: coverageChecking } = useAreaCoverageProbe({
    lat: location?.lat ?? null,
    lng: location?.lng ?? null,
    enabled: hasCoords && currentScreen >= SCREEN.location,
  });
  const picksVariant: "picks" | "waitlist" | "checking" =
    !hasCoords ? "picks" : coverageChecking ? "checking" : hasCoverageNearby === false ? "waitlist" : "picks";

  // ─── PostHog: step viewed (once per screen and variant) ───
  const lastViewedRef = useRef<string | null>(null);
  useEffect(() => {
    if (awaitingConfirmation) return;
    if (currentScreen === SCREEN.picks && picksVariant === "checking") return;
    const step = stepNameFor(currentScreen, picksVariant === "waitlist" ? "waitlist" : "picks");
    if (!step) return;
    const key = `${currentScreen}:${step}`;
    if (lastViewedRef.current === key) return;
    lastViewedRef.current = key;
    // A signed in person who lands on Welcome is a stray view, not a funnel start.
    if (currentScreen === SCREEN.welcome && wasInitiallyAuthenticated.current && !isForceOnboarding.current) return;
    capture("onboarding_step_viewed", { step });
  }, [currentScreen, picksVariant, awaitingConfirmation]);

  const completeStep = (step: string, extra?: Record<string, unknown>) => capture("onboarding_step_completed", { step, ...extra });
  const skipStep = (step: string) => capture("onboarding_step_skipped", { step });

  // ─── The single finish moment ───
  const finalizeRef = useRef<() => Promise<void>>(async () => {});
  const finishStepFiredRef = useRef(false);
  const finalizeAndNavigate = useCallback(async () => {
    const uid = user?.id;
    if (uid) {
      let failed = false;
      try {
        // Fill null only: the first finish wins, later devices never move it.
        const { error } = await supabase
          .from("profiles")
          .update({ onboarding_completed_at: new Date().toISOString() })
          .eq("id", uid)
          .is("onboarding_completed_at", null);
        failed = !!error;
      } catch {
        failed = true;
      }
      if (failed) {
        toast.error("We couldn't finish setting up your account.", {
          description: "Check your connection and try again.",
          duration: Infinity,
          action: { label: "Try again", onClick: () => { void finalizeRef.current(); } },
        });
        return;
      }
    }
    inOnboardingRef.current = false;
    // Idempotent completion: only the first time per device.
    const alreadyComplete = isOnboardingCompleteOnDevice();
    markOnboardingCompleteOnDevice(); // also clears the in progress marker
    if (!alreadyComplete) {
      capture("onboarding_completed");
      if (!finishStepFiredRef.current) {
        finishStepFiredRef.current = true;
        completeStep("celebration");
      }
    }
    clearPendingTerms();
    navigate("/browse");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, user?.id]);
  finalizeRef.current = finalizeAndNavigate;

  const exitWithoutCompleting = useCallback((opts?: { respectPushTarget?: boolean }) => {
    inOnboardingRef.current = false;
    markOnboardingCompleteOnDevice();
    if (opts?.respectPushTarget && (getAnyPushTarget() || window.location.pathname !== "/")) return;
    navigate("/browse", { replace: true });
  }, [navigate]);

  // Mark "account step reached" on this device so a mid flow close resumes.
  useEffect(() => {
    if (currentScreen === SCREEN.account) {
      try { localStorage.setItem("grounded_onboarding_in_progress", "1"); } catch { /* ignore */ }
    }
  }, [currentScreen]);

  // ─── Back ───
  const goBack = useCallback(() => {
    if (awaitingConfirmation) return;
    if (currentScreen === SCREEN.account && emailMode) { setEmailMode(false); return; }
    if (!hasBackArrow(currentScreen)) return;
    // Notifications goes back to About you; everything else one step.
    goTo(currentScreen - 1);
  }, [awaitingConfirmation, currentScreen, emailMode, goTo]);

  const swipe = useSwipeGesture({
    onSwipeLeft: () => {
      // Only the Score screen moves forward by swipe; every other step has a
      // button with its own rules (About you needs its three answers).
      if (awaitingConfirmation) return;
      if (currentScreen === SCREEN.score) { completeStep("grounded_score"); goTo(SCREEN.priorities); }
    },
    onSwipeRight: () => {
      if (awaitingConfirmation) return;
      if (hasBackArrow(currentScreen)) goBack();
    },
    threshold: 50,
  });

  // ─── Preferences ───
  const savePreferences = async (
    userId: string,
    priorities: string[],
    demographics?: { occupation?: string; ageRange?: string; cafeFrequency?: string; referralSource?: string },
    options?: { fillNullsOnly?: boolean },
  ): Promise<boolean> => {
    try {
      const finalImportance = priorities.length > 0 ? prioritiesToImportance(priorities) : {};
      const answers = sanitizeAboutYou({
        occupation: demographics?.occupation ?? "",
        ageRange: demographics?.ageRange ?? "",
        cafeFrequency: demographics?.cafeFrequency ?? "",
        referralSource: demographics?.referralSource ?? "",
      });
      let payload: Record<string, unknown> = { user_id: userId, ...finalImportance, ...answers };
      if (options?.fillNullsOnly) {
        const columns = Object.keys(payload).filter((k) => k !== "user_id");
        if (columns.length === 0) return true;
        const { data: existing } = await supabase
          .from("user_preferences")
          .select(columns.join(","))
          .eq("user_id", userId)
          .maybeSingle();
        payload = filterToNullColumns(payload, existing as unknown as Record<string, unknown> | null);
        if (hasNoAnswers(payload)) return true;
      }
      if (Object.keys(payload).length <= 1) return true;
      const { error } = await supabase.from("user_preferences").upsert(payload as never, { onConflict: "user_id" });
      if (error) throw error;
      setUserSegmentProperties({
        occupation: payload.occupation as string | undefined,
        ageRange: payload.age_range as string | undefined,
        cafeFrequency: payload.cafe_frequency as string | undefined,
      });
      return true;
    } catch (error) {
      console.error("Failed to save preferences:", error);
      return false;
    }
  };

  // Priorities are the only answers given before the account; cache them for
  // the sign in that reloads the app. `userId` is only known for email sign up.
  const cachePendingPreferences = (userId?: string) => {
    try {
      localStorage.setItem(PENDING_PREFERENCES_KEY, JSON.stringify({
        motivations: [],
        priorities: selectedWorkPriorities,
        ...(userId ? { userId } : {}),
        timestamp: Date.now(),
      }));
    } catch { /* ignore */ }
  };

  // ─── Auth listener: preference replay, referral safety net, advance ───
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event !== "SIGNED_IN" || !session?.user) return;
      const referralCode = getStoredReferralCode();
      if (referralCode) {
        const uid = session.user.id;
        // Deferred: Supabase holds the auth lock during this callback.
        setTimeout(() => {
          supabase
            .from("profiles")
            .update({ referral_code: referralCode, referred_at: new Date().toISOString() })
            .eq("id", uid)
            .is("referral_code", null)
            .select("id")
            .then(({ error }) => { if (!error) clearStoredReferralCode(); });
        }, 0);
      }

      const pendingPrefs = localStorage.getItem(PENDING_PREFERENCES_KEY);
      if (pendingPrefs) {
        try {
          const { priorities, occupation, ageRange, cafeFrequency, referralSource, timestamp, userId } = JSON.parse(pendingPrefs);
          const ownedByThisUser = typeof userId === "string" && userId === session.user.id;
          const ownedBySomeoneElse = typeof userId === "string" && userId !== session.user.id;
          const withinTtl = typeof timestamp === "number" && Date.now() - timestamp < PENDING_PREFERENCES_TTL_MS;
          if (ownedByThisUser || (!ownedBySomeoneElse && withinTtl)) {
            setTimeout(async () => {
              await savePreferences(session.user.id, priorities || [], { occupation, ageRange, cafeFrequency, referralSource }, { fillNullsOnly: true });
              localStorage.removeItem(PENDING_PREFERENCES_KEY);
            }, 0);
          } else {
            localStorage.removeItem(PENDING_PREFERENCES_KEY);
          }
        } catch {
          localStorage.removeItem(PENDING_PREFERENCES_KEY);
        }
      }

      // The account now exists: About you is next for every sign up method.
      if (inOnboardingRef.current && currentScreenRef.current <= SCREEN.account) {
        completeStep("auth");
        toast.success("Account created!");
        setAwaitingConfirmation(false);
        setEmailMode(false);
        setSlideDirection("left");
        setCurrentScreen(SCREEN.aboutYou);
      } else if (!inOnboardingRef.current) {
        toast.success("Welcome to Grounded!");
        exitWithoutCompleting();
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Sign up ───
  const passwordChecks = PASSWORD_REQUIREMENTS.map((r) => ({ id: r.id, label: r.label, ok: r.test(password) }));
  const rulesOk = passwordChecks.every((c) => c.ok);
  // Checked once every other rule is met, so the list warns before the tap.
  const leakState = usePasswordLeakCheck(password, rulesOk);
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const signUpMissing = !firstName.trim()
    ? "Add your first name to continue."
    : !emailOk
      ? "Add a valid email to continue."
      : !rulesOk
        ? "Your password needs every item checked."
        : leakState === "leaked"
          ? "Pick a password that hasn't been leaked."
          : leakState === "checking"
            ? "Checking your password…"
            : null;

  const handleSignUp = async () => {
    if (signUpMissing || isLoading) return;
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    setIsLoading(true);
    // The terms line was on screen for this sign up.
    setPendingTerms();
    const referralCode = getStoredReferralCode();
    // The name goes in the sign up metadata so the profile has it from the
    // start. The last name is optional and never filled from the first name.
    const signUpMeta: Record<string, unknown> = {
      first_name: trimmedFirst,
      ...(trimmedLast ? { last_name: trimmedLast } : {}),
      display_name: `${trimmedFirst} ${trimmedLast}`.trim(),
      ...(referralCode ? { referral_code: referralCode } : {}),
    };
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { emailRedirectTo: `${window.location.origin}/`, data: signUpMeta },
    });
    if (error) {
      if (error.message?.toLowerCase().includes("already registered")) {
        toast.error("This email is already registered.", { description: "Redirecting you to sign in…", duration: 3000 });
        setTimeout(() => navigate("/auth", { state: { from: "/browse", prefillEmail: email } }), 1500);
      } else {
        toast.error(friendlyPasswordError(error));
      }
      setIsLoading(false);
      return;
    }
    if (data.user) {
      localStorage.setItem("grounded_just_signed_up", "1");
      // Owned by this account, so it also holds through a late confirmation.
      setPendingTerms(data.user.id);
      if (data.session) {
        await savePreferences(data.user.id, selectedWorkPriorities);
      } else {
        cachePendingPreferences(data.user.id);
        capture("onboarding_awaiting_email_confirmation");
        setAwaitingConfirmation(true);
      }
    }
    setIsLoading(false);
  };

  const handleResendConfirmation = async () => {
    if (resending) return;
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({ type: "signup", email });
      if (error) throw error;
      toast.success("Confirmation email sent again. Check your inbox.");
    } catch (e: unknown) {
      toast.error((e as { message?: string })?.message ?? "Couldn't resend right now. Try again in a moment.");
    } finally {
      setResending(false);
    }
  };

  const startNativeOAuth = async (provider: "google" | "apple") => {
    const { error } = await lovable.auth.signInWithOAuth(provider, { redirect_uri: `${getOAuthRedirectUri()}/native-callback` });
    if (error) throw error;
  };

  const handleOAuth = async (provider: "google" | "apple") => {
    setIsLoading(true);
    cachePendingPreferences();
    setPendingTerms();
    try {
      if (isNativeShell()) { await startNativeOAuth(provider); return; }
      if (provider === "google") {
        const popup = await tryPopupOAuth("google");
        if (popup.handled) {
          if (popup.error) throw popup.error;
          return;
        }
      }
      const { error } = await lovable.auth.signInWithOAuth(provider, { redirect_uri: getOAuthRedirectUri() });
      if (error) throw error;
    } catch (error: unknown) {
      toast.error((error as { message?: string })?.message ?? "Sign in failed.");
      localStorage.removeItem(PENDING_PREFERENCES_KEY);
      clearPendingTerms();
    } finally {
      setTimeout(() => setIsLoading(false), 15000);
    }
  };

  const handleSignInExisting = () => {
    cachePendingPreferences();
    setPendingTerms();
    navigate("/auth", { state: { from: "/browse" } });
  };

  // ─── Score animation ───
  useEffect(() => {
    if (currentScreen !== SCREEN.score) return;
    setScoreAnimated(false);
    setDisplayScore(0);
    setScoreKey((k) => k + 1);
    const timer = setTimeout(() => {
      setScoreAnimated(true);
      const start = performance.now();
      const duration = 800;
      const animate = (now: number) => {
        const progress = Math.min((now - start) / duration, 1);
        setDisplayScore(Math.round(87 * (1 - Math.pow(1 - progress, 3))));
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }, 50);
    return () => clearTimeout(timer);
  }, [currentScreen]);

  // ─── About you: prefill saved answers on a resume ───
  useEffect(() => {
    if (currentScreen !== SCREEN.aboutYou || !user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from("user_preferences")
          .select("occupation, age_range, cafe_frequency, referral_source")
          .eq("user_id", user.id)
          .maybeSingle();
        if (cancelled || !data) return;
        const s = sanitizeAboutYou({
          occupation: (data as { occupation?: string }).occupation ?? "",
          ageRange: (data as { age_range?: string }).age_range ?? "",
          cafeFrequency: (data as { cafe_frequency?: string }).cafe_frequency ?? "",
          referralSource: (data as { referral_source?: string }).referral_source ?? "",
        });
        if (s.occupation) setSelectedOccupation((c) => c || s.occupation!);
        if (s.age_range) setSelectedAgeRange((c) => c || s.age_range!);
        if (s.cafe_frequency) setSelectedCafeFrequency((c) => c || s.cafe_frequency!);
        if (s.referral_source) setSelectedReferralSource((c) => c || s.referral_source!);
      } catch { /* leave empty */ }
    })();
    return () => { cancelled = true; };
  }, [currentScreen, user?.id]);

  /**
   * The username is picked for them from their name and saved before Finish
   * shows. No name at all (Apple can hide it) gives "coffee", "coffee2" and so
   * on. A handle taken in the meantime picks the next free one.
   */
  const ensureUsername = async (uid: string): Promise<string | null> => {
    try {
      const { data } = await supabase.from("profiles").select("first_name, last_name, username").eq("id", uid).maybeSingle();
      const row = (data ?? {}) as { first_name?: string | null; last_name?: string | null; username?: string | null };
      if (row.username) return row.username;
      const meta = nameFromMetadata(user?.user_metadata as Record<string, unknown> | undefined);
      const first = row.first_name || meta.first || "";
      const last = row.last_name || meta.last || "";
      for (let attempt = 0; attempt < 3; attempt++) {
        const candidate = await suggestUsername(supabase, first, last);
        const { error } = await supabase.from("profiles").update({ username: candidate }).eq("id", uid);
        if (!error) return candidate;
        if ((error as { code?: string }).code !== "23505") break;
      }
      const fallback = fallbackHandleFromId(uid);
      const { error } = await supabase.from("profiles").update({ username: fallback }).eq("id", uid);
      return error ? null : fallback;
    } catch {
      return null;
    }
  };

  const handleAboutYouContinue = async () => {
    if (aboutYouMissingLine(aboutAnswers) || aboutSaving) return;
    const uid = user?.id;
    setAboutSaving(true);
    try {
      if (uid) {
        const ok = await savePreferences(uid, [], aboutAnswers);
        if (!ok) {
          toast.error("We couldn't save your answers.", { description: "Check your connection and try again." });
          return;
        }
      }
      completeStep("demographics", {
        occupation: selectedOccupation,
        age_range: selectedAgeRange,
        referral_source: selectedReferralSource || null,
        $set: {
          occupation: selectedOccupation,
          age_range: selectedAgeRange,
          cafe_frequency: selectedCafeFrequency,
          ...(selectedReferralSource ? { referral_source: selectedReferralSource } : {}),
        },
      });
      completeStep("habits", { cafe_frequency: selectedCafeFrequency });
      if (uid) setFinishUsername(await ensureUsername(uid));
      goTo(shouldOfferNotificationsScreen() ? SCREEN.notifications : SCREEN.finish);
    } finally {
      setAboutSaving(false);
    }
  };

  const aboutAnswers = {
    occupation: selectedOccupation,
    ageRange: selectedAgeRange,
    cafeFrequency: selectedCafeFrequency,
    referralSource: selectedReferralSource,
  };

  const justLooking = (step: string) => {
    capture("onboarding_guest_browse");
    capture("onboarding_skipped", { step });
    clearPendingTerms();
    markGuestBrowse();
    try { localStorage.setItem("grounded_welcome_seen", "true"); } catch { /* ignore */ }
    navigate("/browse");
  };

  const firstPick = useMemo(() => (currentScreen === SCREEN.account ? getPendingPicks()[0] ?? null : null), [currentScreen]);

  // ═══════════════════════════════════════════════
  // SCREENS
  // ═══════════════════════════════════════════════

  const renderWelcome = () => (
    <ScreenFrame
      testId="welcome-step"
      dock={
        <>
          <OButton onClick={() => { completeStep("welcome"); goTo(SCREEN.score); }}>Find my spot →</OButton>
          <TextLink onClick={() => justLooking("welcome")}>Just looking? Explore cafés →</TextLink>
          {!isNativeApp() && (
            <div className="flex justify-center mt-4"><AppStoreBadge className="w-[120px] mx-auto" /></div>
          )}
          <p className="text-center mt-4" style={{ fontSize: 13, color: "#A08A72" }}>
            Already a member?{" "}
            <button onClick={() => { clearPendingTerms(); navigate("/auth", { state: { from: "/browse" } }); }} style={{ color: "#3B2314", fontWeight: 600 }}>
              Sign in
            </button>
          </p>
        </>
      }
    >
      <div className="flex flex-col min-h-full">
      <div className="flex items-center gap-2 mb-3">
        <img src={mascotNormal} alt="" aria-hidden="true" className="h-7 w-auto" data-testid="welcome-brand-mark" />
        <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: OC.cta, letterSpacing: "0.06em" }}>grounded</span>
      </div>
      <div style={{ height: 0.5, backgroundColor: "#E6DAC9" }} />
      <p className="mt-5 mb-4 whitespace-nowrap" style={{ textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 10, color: OC.muted }}>
        For remote workers · students · freelancers
      </p>
      <StepHeading>Stop guessing. Start working.</StepHeading>
      <StepLine className="max-w-[280px] !mt-3">Every café scored on what actually matters when you sit down to work.</StepLine>

      <div className="relative flex-1" style={{ marginTop: 96, minHeight: "min(184px, 24vh)", maxHeight: 340 }}>
        <WelcomeGroundy />
        {welcomeCafe.photo ? (
          <img
            src={welcomeCafe.photo}
            alt={welcomeCafe.name}
            className="absolute inset-0 block w-full h-full object-cover"
            style={{ borderRadius: 14 }}
            loading="eager"
          />
        ) : (
          <div aria-hidden="true" className="absolute inset-0 w-full h-full" style={{ borderRadius: 14, backgroundColor: OC.tile }} data-testid="welcome-photo-placeholder" />
        )}
        {welcomeCafe.score != null && (
        <div
          className="absolute flex flex-col items-center justify-center rounded-full"
          style={{ left: 10, bottom: -18, width: 76, height: 76, background: "#FDF8F0", border: "1.5px solid #C4A882" }}
          data-testid="welcome-score"
        >
          <span style={{ fontSize: 6.5, letterSpacing: "0.18em", color: OC.muted }}>GROUNDED</span>
          <span style={{ fontSize: 22, fontWeight: 700, color: "#3B2314", lineHeight: 1 }}>{welcomeCafe.score}</span>
          <span style={{ fontSize: 6.5, letterSpacing: "0.18em", color: OC.muted }}>SCORE</span>
        </div>
        )}
      </div>
      <div style={{ marginTop: 28 }}>
        <div style={{ fontWeight: 600, color: "#3B2314", fontSize: 17 }}>{welcomeCafe.name}</div>
        <div style={{ fontSize: 13, color: OC.muted, marginTop: 2 }}>{welcomeCafe.neighborhood}</div>
      </div>
      <div aria-hidden="true" style={{ height: 12 }} />
      </div>
    </ScreenFrame>
  );

  const renderScore = () => {
    const circumference = 2 * Math.PI * 44;
    const strokeProgress = scoreAnimated ? circumference * (1 - 87 / 100) : circumference;
    return (
      <ScreenFrame
        testId="score-step"
        dock={<OButton onClick={() => { completeStep("grounded_score"); goTo(SCREEN.priorities); }}>Got it →</OButton>}
      >
        <StepHeader groundy={GROUNDY.score} eyebrow="The Grounded Score™" />
        <StepHeading>A score built for working.</StepHeading>
        <StepLine>
          Every café gets a score out of 100, built from WiFi, outlets, seating and parking. At 80 or above, there's always WiFi and outlets.
        </StepLine>
        <div className="relative mx-auto mt-8" style={{ width: 120, height: 120 }}>
          <svg className="w-full h-full -rotate-90" viewBox="0 0 96 96" aria-hidden="true">
            <circle cx="48" cy="48" r="44" fill="none" stroke={OC.track} strokeWidth="4" />
            <circle cx="48" cy="48" r="44" fill="none" stroke="#7B4F2E" strokeWidth="4" strokeLinecap="round"
              strokeDasharray={circumference} strokeDashoffset={strokeProgress} style={{ transition: "stroke-dashoffset 800ms ease-out" }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span style={{ fontSize: 38, fontWeight: 700, color: OC.text }}>{displayScore}</span>
            <span style={{ fontSize: 7, letterSpacing: "0.15em", color: OC.muted }}>EXCELLENT</span>
          </div>
        </div>
        {/* Example café grades in a labeled 2 by 2 grid, same chips Browse uses. */}
        <GradeStrip key={scoreKey} amenities={SAMPLE_87_AMENITIES} labeled className="grid grid-cols-2 gap-3 mt-8" />
      </ScreenFrame>
    );
  };

  const renderPriorities = () => {
    const tiles = [
      { id: "wifi", art: GROUNDY.tileWifi, label: "Fast WiFi", sub: "Non-negotiable" },
      { id: "outlets", art: GROUNDY.tileOutlets, label: "Plenty of outlets", sub: "Stay charged all day" },
      { id: "seating", art: GROUNDY.tileSeating, label: "Good seating", sub: "Room to settle in" },
      { id: "parking", art: GROUNDY.tileParking, label: "Easy parking", sub: "Stress-free arrival" },
    ];
    const toggle = (id: string) =>
      setSelectedWorkPriorities((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
    const picked = selectedWorkPriorities.length > 0;
    return (
      <ScreenFrame
        testId="priorities-step"
        dock={
          picked ? (
            <OButton onClick={() => { completeStep("priorities"); goTo(SCREEN.location); }}>Continue →</OButton>
          ) : (
            <OButton kind="outline" onClick={() => { skipStep("priorities"); goTo(SCREEN.location); }}>Skip for now</OButton>
          )
        }
      >
        <StepHeader groundy={GROUNDY.priorities} eyebrow="Your priorities" />
        <StepHeading>What matters most?</StepHeading>
        <StepLine>
          Pick everything that applies. We'll use these to rank cafés for you and tag your{" "}
          <span className="whitespace-nowrap">strongest matches <ForYouTag />.</span>
        </StepLine>
        <div className="grid grid-cols-2 gap-3 w-full mt-5">
          {tiles.map((t) => {
            const selected = selectedWorkPriorities.includes(t.id);
            return (
              <button
                key={t.id}
                onClick={() => toggle(t.id)}
                aria-pressed={selected}
                data-testid={`priority-${t.id}`}
                className="relative flex flex-col items-center justify-center text-center rounded-[14px] px-3 py-4 min-h-[150px] transition-all duration-200 active:scale-95"
                style={{
                  backgroundColor: selected ? OC.white : OC.tile,
                  border: `1.5px solid ${selected ? OC.fill : "transparent"}`,
                }}
              >
                {selected && (
                  <span className="absolute top-2 right-2 h-5 w-5 rounded-full flex items-center justify-center" style={{ backgroundColor: OC.fill }} data-testid="priority-check">
                    <Check size={12} color={OC.white} strokeWidth={3} />
                  </span>
                )}
                <img src={t.art} alt="" aria-hidden="true" className="h-[80px] w-auto object-contain" />
                <span className="mt-2" style={{ fontSize: 14, fontWeight: 600, color: OC.text }}>{t.label}</span>
                <span style={{ fontSize: 11, color: OC.muted }}>{t.sub}</span>
              </button>
            );
          })}
        </div>
      </ScreenFrame>
    );
  };

  const renderLocation = () => (
    <LocationStep
      onDone={(loc, method) => {
        markLocationAnswered();
        if (loc) { setPendingLocation(loc); } else { clearPendingLocation(); }
        setLocation(loc);
        capture("onboarding_location_set", { method });
        completeStep("location", { method });
        goTo(SCREEN.picks);
      }}
    />
  );

  const renderPicksOrWaitlist = () => {
    if (picksVariant === "checking") {
      return (
        <div className="flex-1 flex items-center justify-center" data-testid="picks-checking">
          <Loader2 className="animate-spin" color={OC.muted} />
        </div>
      );
    }
    if (picksVariant === "waitlist" && location && hasCoords) {
      return (
        <WaitlistStep
          location={location as HomeLocation & { lat: number; lng: number }}
          onChange={() => goTo(SCREEN.location)}
          onContinue={() => { completeStep("waitlist"); goTo(SCREEN.account); }}
          onSkip={() => { skipStep("waitlist"); goTo(SCREEN.account); }}
          onJustLooking={() => justLooking("waitlist")}
        />
      );
    }
    return (
      <PicksStep
        geo={hasCoords ? { lat: location!.lat as number, lng: location!.lng as number } : null}
        priorities={selectedWorkPriorities}
        onContinue={() => { completeStep("picks"); goTo(SCREEN.account); }}
        onSkip={() => { skipStep("picks"); goTo(SCREEN.account); }}
      />
    );
  };

  const accountHeader = (
    <>
      <StepHeader groundy={GROUNDY.account} eyebrow="Your account" />
      <StepHeading>Claim your spot.</StepHeading>
      <StepLine>Keep your saved cafés and leave reviews of your own.</StepLine>
    </>
  );

  const signInLine = (
    <p className="text-center mt-4" style={{ fontSize: 13, color: OC.textSec }}>
      Already have an account?{" "}
      <button onClick={handleSignInExisting} style={{ color: "#3B2314", fontWeight: 700 }}>Sign in</button>
    </p>
  );

  const renderAccount = () => {
    if (emailMode) {
      const inputStyle: React.CSSProperties = { backgroundColor: OC.white, border: `0.5px solid ${OC.divider}`, padding: "0 14px", fontSize: 16, color: OC.text };
      return (
        <ScreenFrame
          testId="account-email-step"
          dock={
            <>
              {signUpMissing && <MissingLine>{signUpMissing}</MissingLine>}
              <OButton onClick={handleSignUp} disabled={!!signUpMissing || isLoading}>
                {isLoading ? <Loader2 size={18} className="animate-spin" /> : "Create account"}
              </OButton>
              <TermsLine />
              {signInLine}
            </>
          }
        >
          {accountHeader}
          <div className="flex flex-col gap-2 mt-5">
            <input type="text" placeholder="First name" aria-label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={50} autoComplete="given-name" className="w-full h-12 rounded-xl outline-none" style={inputStyle} />
            <input type="text" placeholder="Last name (optional)" aria-label="Last name (optional)" value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={50} autoComplete="family-name" className="w-full h-12 rounded-xl outline-none" style={inputStyle} />
            <div className="relative">
              <Mail size={16} color={OC.muted} className="absolute left-[14px] top-1/2 -translate-y-1/2" />
              <input type="email" placeholder="you@example.com" aria-label="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" className="w-full h-12 rounded-xl outline-none" style={{ ...inputStyle, paddingLeft: 40 }} />
            </div>
            <div className="relative">
              <Lock size={16} color={OC.muted} className="absolute left-[14px] top-1/2 -translate-y-1/2" />
              <input type={showPassword ? "text" : "password"} placeholder="Password" aria-label="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" className="w-full h-12 rounded-xl outline-none" style={{ ...inputStyle, padding: "0 40px" }} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-[14px] top-1/2 -translate-y-1/2" aria-label={showPassword ? "Hide password" : "Show password"}>
                {showPassword ? <EyeOff size={16} color={OC.muted} /> : <Eye size={16} color={OC.muted} />}
              </button>
            </div>
            <ul className="grid grid-cols-2 gap-x-3 gap-y-1 mt-1 px-1" data-testid="password-checklist">
              {passwordChecks.map((c) => (
                <li key={c.id} className="flex items-center gap-1.5" style={{ fontSize: 12, color: c.ok ? "#3F6B4C" : OC.textSec }} data-ok={c.ok}>
                  {c.ok ? <Check size={12} /> : <Circle size={10} />}
                  <span>{c.label}</span>
                </li>
              ))}
              <li
                className="col-span-2 flex items-center gap-1.5"
                style={{ fontSize: 12, color: leakState === "clear" ? "#3F6B4C" : leakState === "leaked" ? "#B4472F" : OC.textSec }}
                data-ok={leakState === "clear"}
                data-testid="password-leak-check"
                data-state={leakState}
              >
                {leakState === "clear" ? <Check size={12} />
                  : leakState === "leaked" ? <X size={12} />
                  : leakState === "checking" ? <Loader2 size={11} className="animate-spin" />
                  : <Circle size={10} />}
                <span>{LEAK_CHECK_LABEL[leakState]}</span>
              </li>
            </ul>
          </div>
        </ScreenFrame>
      );
    }
    return (
      <ScreenFrame
        testId="account-step"
        dock={
          <>
            <div className="flex flex-col gap-2">
              <OButton kind="dark" onClick={() => handleOAuth("apple")} disabled={isLoading}><AppleLogo />Continue with Apple</OButton>
              <OButton kind="light" onClick={() => handleOAuth("google")} disabled={isLoading}><GoogleLogo />Continue with Google</OButton>
              <OButton kind="outline" onClick={() => setEmailMode(true)} disabled={isLoading}><Mail size={16} />Sign up with email</OButton>
            </div>
            <TermsLine />
            {signInLine}
          </>
        }
      >
        {accountHeader}
        {firstPick && (
          <div className="mt-5" data-testid="account-first-pick">
            <PickCard name={firstPick.name} area={firstPick.area} score={firstPick.score} grades={firstPick.grades ?? null} forYou={!!firstPick.forYou} saved subline="Saved to Favorites" />
          </div>
        )}
      </ScreenFrame>
    );
  };

  const renderConfirmEmail = () => (
    <ScreenFrame
      testId="confirm-email-step"
      dock={
        <>
          <OButton onClick={handleResendConfirmation} disabled={resending}>
            {resending ? <Loader2 size={18} className="animate-spin" /> : "Resend email"}
          </OButton>
          <TextLink onClick={() => navigate("/auth", { state: { from: "/browse", prefillEmail: email } })}>Go to sign in</TextLink>
          <p className="text-center mt-4" style={{ fontSize: 12, color: OC.muted }}>
            Wrong email?{" "}
            <button onClick={() => setAwaitingConfirmation(false)} style={{ color: "#7B4F2E", fontWeight: 500 }}>Go back</button>
          </p>
        </>
      }
    >
      <StepHeader groundy={GROUNDY.account} eyebrow="Almost there" />
      <StepHeading>Check your inbox.</StepHeading>
      <StepLine>We sent a confirmation link to</StepLine>
      <p style={{ fontSize: 15, fontWeight: 600, color: OC.text, marginTop: 2 }}>{email}</p>
      <StepLine>
        {`Tap the link in that email to confirm your account${firstName.trim() ? `, ${firstName.trim()}` : ""}. Your picks and answers are kept, and you'll pick up right where you left off.`}
      </StepLine>
    </ScreenFrame>
  );

  const renderAboutYou = () => {
    const missing = aboutYouMissingLine(aboutAnswers);
    const chip = (selected: boolean, label: string, onClick: () => void, key: string) => (
      <button
        key={key}
        onClick={onClick}
        aria-pressed={selected}
        className="px-4 py-2 min-h-[40px] rounded-full transition-all duration-200 ease-out active:scale-95 text-center"
        style={{
          backgroundColor: selected ? OC.cta : OC.tile,
          color: selected ? OC.ctaText : OC.text,
          fontSize: 14,
          fontWeight: 500,
          border: `1.5px solid ${selected ? OC.cta : "transparent"}`,
        }}
      >
        {label}
      </button>
    );
    const question = (text: React.ReactNode) => (
      <p className="w-full text-left mt-4 mb-2" style={{ fontSize: 14, fontWeight: 500, color: OC.text }}>{text}</p>
    );
    return (
      <ScreenFrame
        testId="demographics-layout"
        fade
        dock={
          <div data-testid="demographics-continue-dock">
            {missing && <MissingLine>{missing}</MissingLine>}
            <OButton onClick={handleAboutYouContinue} disabled={!!missing || aboutSaving}>
              {aboutSaving ? <Loader2 size={18} className="animate-spin" /> : "Continue →"}
            </OButton>
          </div>
        }
      >
        <div data-testid="demographics-questions">
          <StepHeader groundy={GROUNDY.aboutYou} eyebrow="Just a few quick things" />
          <StepHeading>Tell us about yourself.</StepHeading>
          <StepLine>So we know who we're building Grounded for.</StepLine>
          {question("What best describes you?")}
          <div className="flex flex-wrap gap-2 w-full">
            {OCCUPATION_OPTIONS.map((t) => chip(selectedOccupation === t.id, t.label, () => setSelectedOccupation(t.id), t.id))}
          </div>
          {question("How old are you?")}
          <div className="flex flex-wrap gap-2 w-full">
            {AGE_RANGE_OPTIONS.map((r) => chip(selectedAgeRange === r, r, () => setSelectedAgeRange(r), r))}
          </div>
          {question("How often do you work from cafés?")}
          <div className="flex flex-wrap gap-2 w-full">
            {FREQUENCY_OPTIONS.map((o) => chip(selectedCafeFrequency === o.id, o.label, () => setSelectedCafeFrequency(o.id), o.id))}
          </div>
          {question(<>How did you hear about Grounded? <span style={{ color: OC.muted, fontWeight: 400 }}>(optional)</span></>)}
          <div className="flex flex-wrap gap-2 w-full pb-6">
            {REFERRAL_OPTIONS.map((o) => chip(selectedReferralSource === o.id, o.label, () => setSelectedReferralSource(selectedReferralSource === o.id ? "" : o.id), o.id))}
          </div>
        </div>
      </ScreenFrame>
    );
  };

  const renderScreen = () => {
    if (awaitingConfirmation) return renderConfirmEmail();
    switch (currentScreen) {
      case SCREEN.welcome: return renderWelcome();
      case SCREEN.score: return renderScore();
      case SCREEN.priorities: return renderPriorities();
      case SCREEN.location: return renderLocation();
      case SCREEN.picks: return renderPicksOrWaitlist();
      case SCREEN.account: return renderAccount();
      case SCREEN.aboutYou: return renderAboutYou();
      case SCREEN.notifications:
        return <NotificationsScreen variant="after_about_you" onDone={() => { completeStep("notifications"); goTo(SCREEN.finish); }} />;
      case SCREEN.finish: return <FinishScreen onFinish={finalizeAndNavigate} username={finishUsername} />;
      default: return null;
    }
  };

  const showBack = !awaitingConfirmation && (hasBackArrow(currentScreen) || (currentScreen === SCREEN.account && emailMode));
  const progress = awaitingConfirmation ? null : progressFor(currentScreen);
  const showTopBar = currentScreen !== SCREEN.welcome && currentScreen !== SCREEN.finish;

  return (
    <div
      className="min-h-dvh flex items-start md:items-center justify-center"
      style={{ backgroundColor: OC.bg, fontFamily: SANS }}
      onTouchStart={swipe.onTouchStart}
      onTouchMove={swipe.onTouchMove}
      onTouchEnd={swipe.onTouchEnd}
    >
      <div
        className={cn(
          "w-full h-dvh flex flex-col",
          "md:h-[760px] md:max-h-[calc(100dvh-64px)] md:max-w-xl md:rounded-3xl md:border-[0.5px] md:my-8",
          "lg:max-w-[520px] lg:shadow-[0_8px_40px_rgba(44,24,16,0.08)]",
        )}
        style={{ backgroundColor: OC.bg, borderColor: OC.divider }}
      >
        <div className="flex flex-col flex-1 min-h-0 px-6 md:px-9 lg:px-12 pt-[calc(20px+env(safe-area-inset-top))] md:pt-8 pb-[calc(16px+env(safe-area-inset-bottom))] md:pb-8">
          {showTopBar && <StepTopBar progress={progress} onBack={showBack ? goBack : undefined} />}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
            <div
              key={awaitingConfirmation ? "confirm" : `${currentScreen}:${emailMode ? "email" : ""}`}
              className="flex flex-col flex-1 min-h-0"
              style={{ animation: `${slideDirection === "left" ? "slideInRight" : "slideInLeft"} 300ms ease-in-out` }}
            >
              {renderScreen()}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes slideInRight { from { transform: translateX(60px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes slideInLeft { from { transform: translateX(-60px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @media (prefers-reduced-motion: reduce) {
          @keyframes slideInRight { from { opacity: 1; } to { opacity: 1; } }
          @keyframes slideInLeft { from { opacity: 1; } to { opacity: 1; } }
        }
      `}</style>
    </div>
  );
};

export default OnboardingFlow;
